import { Component, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { isStaleChunkReloadError } from '@/lib/staleChunkReload';

// ============================================================================
// Types
// ============================================================================

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Error boundary to catch JavaScript errors in child components.
 * Displays a fallback UI instead of crashing the whole app.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    // A deploy removed this tab's chunk and the reload is already under way; that is not a fault to report.
    if (isStaleChunkReloadError(error)) return;
    Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center py-12">
          <AlertCircle className="h-12 w-12 text-destructive" />
          <h2 className="mt-4 text-xl font-semibold">Something went wrong</h2>
          <p className="mt-2 text-muted-foreground">
            {this.state.error?.message || 'An unexpected error occurred'}
          </p>
          <Button onClick={this.handleRetry} className="mt-4">
            <RefreshCw className="mr-2 h-4 w-4" />
            Try again
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

// ============================================================================
// Route Error Boundary
// ============================================================================

interface RouteErrorFallbackProps {
  error?: Error;
}

/**
 * Fallback component for lazy-loaded route errors.
 */
export function RouteErrorFallback({ error }: RouteErrorFallbackProps): JSX.Element {
  const handleReload = (): void => {
    window.location.reload();
  };

  return (
    <div className="flex flex-col items-center justify-center py-12">
      <AlertCircle className="h-12 w-12 text-destructive" />
      <h2 className="mt-4 text-xl font-semibold">Failed to load page</h2>
      <p className="mt-2 text-muted-foreground">
        {error?.message || 'The page could not be loaded. Please try again.'}
      </p>
      <Button onClick={handleReload} className="mt-4">
        <RefreshCw className="mr-2 h-4 w-4" />
        Reload page
      </Button>
    </div>
  );
}
